"use client";

import { APPROVAL_TIMEOUT_MS, type MagnusUIMessage, type SdkApprovalDecision, type SdkApprovalRequest } from "@bg-bgi/magnus-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type { ApprovalChoice } from "./approval-input";
import { type ActiveApproval, type ChatApprovalsSource, findPendingApproval } from "./approval-prompt";
const CHOICE_TO_DECISION = {
  once: "allow",
  always: "always",
  "approve-all": "approve-all",
  deny: "deny"
} as const satisfies Record<ApprovalChoice, SdkApprovalDecision>;

/**
 * Watches the transcript for paused, destructive tool calls
 * (`data-approval-request`) and surfaces the first unresolved one as an
 * {@link ActiveApproval} for the block to render in place of the composer.
 * The decision goes through `source.submit`, which resumes the paused
 * workflow; the card is then dismissed via a resolved-id dedupe (the
 * decision is not a chat message, so message order can't collapse it).
 * No `source` → no card is ever surfaced.
 */
export function useApprovalPrompt({
  messages,
  source
}: {
  messages: readonly MagnusUIMessage[] | undefined;
  source: ChatApprovalsSource | undefined;
}): ActiveApproval | null {
  const [pending, setPending] = useState<SdkApprovalRequest | null>(null);
  const [resolved] = useState(() => new Set<string>());
  useEffect(() => {
    if (!source || pending) {
      return;
    }
    const next = findPendingApproval(messages, resolved);
    if (next) {
      setPending(next);
    }
  }, [messages, pending, resolved, source]);
  const submitDecision = useCallback(async (request: SdkApprovalRequest, choice: ApprovalChoice, editedArgs?: Record<string, unknown>): Promise<void> => {
    if (!source) {
      return;
    }
    const decision = CHOICE_TO_DECISION[choice];
    try {
      // Persistence keys are server-computed; the workflow persists its
      // own copy on `always` / `approve-all`. Card edits only accompany
      // allow-decisions.
      await source.submit({
        toolCallId: request.toolCallId,
        decision,
        ...(editedArgs && decision !== "deny" ? {
          editedArgs
        } : {})
      });
      if (decision === "always" || decision === "approve-all") {
        await source.onPersisted?.();
      }
    } catch {
      // The card is not re-armed: the block can't tell a transient
      // failure from a pause that already resolved or expired, and a card
      // for a dead pause would hold the composer hostage. An unresolved
      // pause is auto-denied by the server sweep.
      toast.error("Couldn't send your decision", {
        description: `${request.toolName} was not run. Ask again to retry; an unanswered request is denied after ${Math.round(APPROVAL_TIMEOUT_MS / 60_000)} minutes.`
      });
    }
  }, [source]);
  const handleResolve = useCallback((choice_0: ApprovalChoice, editedArgs_0?: Record<string, unknown>) => {
    if (!pending) {
      return;
    }
    const request_0 = pending;
    resolved.add(request_0.toolCallId);
    setPending(null);
    submitDecision(request_0, choice_0, editedArgs_0).catch(() => undefined);
  }, [pending, resolved, submitDecision]);
  const copy = source?.copy;
  return useMemo<ActiveApproval | null>(() => pending ? {
    toolCallId: pending.toolCallId,
    request: pending,
    copy,
    onResolve: handleResolve
  } : null, [pending, copy, handleResolve]);
}